import { reject } from "../../../../../Common/TypingUtils";
import { DataDeclarationHelper } from "../DataDeclaration/DataDeclarationHelper";
import { MaskResolver } from "../PathUtils/MaskResolver";
import {
    getAllUnitIdInstances,
    SectionName,
    sectionNameToUnitId,
} from "../../../Controls/FormParts/Unit/UnitId/UnitId";
import { BindingPath, ViewModel } from "../CoreTypes";
import {
    AbsoluteModelPath,
    AbsoluteResolvedModelPath,
    createAbsoluteFromMask,
    createAbsoluteResolvedFromTokens,
    PathTokens,
} from "../../../../../Common/ModelPath/ModelPath";
import { AbsoluteModelPathSet } from "../../../../../Common/ModelPath/Set/AbsoluteModelPathSet";
import { IFormReadonlyModelAccessor } from "../../Form/IFormModelAccessor";
import { MatchKind } from "../../../../../Common/ModelPath/PathMatcher";
import { IEquatable } from "../../../../../Common/ModelPath/IEquatable";
import { Set2 } from "../../../../../Common/ModelPath/Set/Set2";
import { getSimpleHashByString } from "../../../../../Common/hashUtils";
import { IExternalEventManager } from "../EventManagement/ExternalEvents/ExternalEventManager";
import { formPageChangedEvent } from "../EventManagement/ExternalEvents/ExternalEvents";

import { ReadonlyModelChunk } from "./ModelChunk";
import { IReadonlyModelHunk } from "./IReadonlyModelHunk";
import { ModelHunk } from "./ModelHunk";

class OptionalSectionKey implements IEquatable<OptionalSectionKey> {
    private readonly instances: string[];
    private readonly sectionName: string;

    public constructor(sectionName: string, instances: string[]) {
        this.sectionName = sectionName;
        this.instances = instances;
    }

    public getHash(): number {
        return (
            getSimpleHashByString(this.sectionName) +
            this.instances.reduce((acc, current) => acc + getSimpleHashByString(current), 0)
        );
    }

    public isEquals(right: OptionalSectionKey): boolean {
        if (this.instances.length != right.instances.length || this.sectionName !== right.sectionName) {
            return false;
        }
        return this.instances.every((item, index) => item === right.instances[index]);
    }
}

interface ISingleUnitInfo {
    path: AbsoluteModelPath;
    requiredInstancesCount: number;
    dependsOnSection?: SectionName;
    optional: boolean;
}

export class Sections {
    private readonly section2masks: ReadonlyMap<SectionName, AbsoluteModelPathSet>;
    private readonly dataDeclarationHelper: DataDeclarationHelper;
    private readonly externalEventManager: IExternalEventManager;
    private readonly allOptionalSections: Map<string, ISingleUnitInfo>;
    private readonly visibleOptionalSections: Set2<OptionalSectionKey>;
    private readonly allSingleSections: Map<string, ISingleUnitInfo>;

    public constructor(dataDeclarationHelper: DataDeclarationHelper, externalEventManager: IExternalEventManager) {
        this.dataDeclarationHelper = dataDeclarationHelper;
        this.externalEventManager = externalEventManager;
        const optionalSections = dataDeclarationHelper.getOptionalSections().map(x => {
            const path = createAbsoluteFromMask(x.path, PathTokens.each);
            return [
                x.id,
                {
                    path: path,
                    requiredInstancesCount: path.getIterationRank(),
                    optional: true,
                },
            ];
        });
        // @ts-ignore
        this.allOptionalSections = new Map(optionalSections);
        const singleSections = dataDeclarationHelper.getAllSingleSections().map(x => {
            const path = createAbsoluteFromMask(x.path, PathTokens.each);
            return [
                x.id,
                {
                    path: path,
                    requiredInstancesCount: path.getIterationRank(),
                    dependsOnSection: x.dependsOnSection as SectionName,
                    optional: x.optional,
                },
            ];
        });
        // @ts-ignore
        this.allSingleSections = new Map(singleSections);
        this.section2masks = dataDeclarationHelper.buildSectionToMasksMap();
        this.visibleOptionalSections = new Set2<OptionalSectionKey>();
    }

    private isPrefixPath(pagePath: AbsoluteModelPath, prefix: AbsoluteModelPath): boolean {
        const prefixParts = prefix.getPathPartsAsArray();
        const pagePathParts = pagePath.getPathPartsAsArray();
        if (pagePathParts.length < prefixParts.length) {
            return false;
        }
        for (const [index, prefixPart] of prefixParts.entries()) {
            const pathPart = pagePathParts[index];
            if (
                !(
                    pathPart === prefixPart ||
                    (PathTokens.isMultiToken(pathPart) && PathTokens.isInstanceToken(prefixPart))
                )
            ) {
                return false;
            }
        }
        return true;
    }

    public markSectionsAsIgnoredByPrefix(prefixes: Iterable<AbsoluteModelPath>): void {
        if (this.allOptionalSections.size > 0) {
            for (const prefix of prefixes) {
                for (const [pageId, info] of this.allOptionalSections.entries()) {
                    if (this.isPrefixPath(info.path, prefix)) {
                        const instances = info.path.matchFor(prefix).reduce<string[]>((acc, curr) => {
                            if (
                                curr.kind === MatchKind.MatchedMultiplicity &&
                                PathTokens.isInstanceToken(curr.target)
                            ) {
                                return [...acc, curr.target];
                            }
                            return acc;
                        }, []);
                        this.markSectionAsIgnoredForced(pageId, info, instances);
                    }
                }
            }
        }
    }

    private markSectionAsIgnoredForced(
        rootSectionName: string,
        sectionInfo: ISingleUnitInfo,
        allInstances: string[]
    ): void {
        if (allInstances.length < sectionInfo.requiredInstancesCount) {
            return;
        }
        const optionalSectionKey = new OptionalSectionKey(
            rootSectionName,
            allInstances.slice(0, sectionInfo.requiredInstancesCount)
        );

        this.visibleOptionalSections.remove(optionalSectionKey);
    }

    private getSingleSectionByNameOrDependencySection(
        sectionName: SectionName
    ): (ISingleUnitInfo & { sectionName: SectionName }) | undefined {
        const sectionInfo = this.allSingleSections.get(sectionName);
        if (sectionInfo == undefined) {
            return undefined;
        }
        if (sectionInfo.dependsOnSection != undefined) {
            const dependencySection = this.allSingleSections.get(sectionInfo.dependsOnSection);

            return dependencySection?.optional === true
                ? {
                      ...dependencySection,
                      sectionName: sectionInfo.dependsOnSection,
                  }
                : undefined;
        }
        return sectionInfo.optional
            ? {
                  ...sectionInfo,
                  sectionName: sectionName,
              }
            : undefined;
    }

    public markSectionAsIgnored(sectionName: SectionName): void {
        const unitId = sectionNameToUnitId(sectionName);
        const rootSectionName = unitId.pageId as SectionName;
        const sectionInfo = this.getSingleSectionByNameOrDependencySection(rootSectionName);
        if (sectionInfo == undefined) {
            return;
        }
        const allInstances = getAllUnitIdInstances(unitId);
        if (allInstances.length < sectionInfo.requiredInstancesCount) {
            return;
        }
        const optionalSectionKey = new OptionalSectionKey(
            sectionInfo.sectionName,
            allInstances.slice(0, sectionInfo.requiredInstancesCount)
        );

        this.visibleOptionalSections.remove(optionalSectionKey);
    }

    public removeSectionFromIgnore(sectionName: SectionName): void {
        const unitId = sectionNameToUnitId(sectionName);
        const rootSectionName = unitId.pageId as SectionName;
        const sectionInfo = this.getSingleSectionByNameOrDependencySection(rootSectionName);
        if (sectionInfo == undefined) {
            return;
        }
        const allInstances = getAllUnitIdInstances(unitId);
        if (allInstances.length < sectionInfo.requiredInstancesCount) {
            return;
        }
        const optionalSectionKey = new OptionalSectionKey(
            sectionInfo.sectionName,
            allInstances.slice(0, sectionInfo.requiredInstancesCount)
        );
        this.visibleOptionalSections.add(optionalSectionKey);
        this.externalEventManager.triggerExternalEvent(formPageChangedEvent(sectionInfo.sectionName));
    }

    public markAllAsIgnored() {
        this.visibleOptionalSections.clear();
    }

    public isSectionIgnored(sectionName: SectionName): boolean {
        const unitId = sectionNameToUnitId(sectionName);
        const rootSectionName = unitId.pageId as SectionName;
        const sectionInfo = this.getSingleSectionByNameOrDependencySection(rootSectionName);
        if (sectionInfo == undefined) {
            return false;
        }
        const allInstances = getAllUnitIdInstances(unitId);

        if (allInstances.length < sectionInfo.requiredInstancesCount) {
            return false;
        }

        const optionalSectionKey = new OptionalSectionKey(
            sectionInfo.sectionName,
            allInstances.slice(0, sectionInfo.requiredInstancesCount)
        );

        return !this.visibleOptionalSections.has(optionalSectionKey);
    }

    public isIgnoredPathByMasks(path: BindingPath | AbsoluteModelPath): boolean {
        if (this.allOptionalSections.size === 0) {
            return false;
        }
        const sections = this.dataDeclarationHelper.getSectionsStrict(path);
        if (sections == undefined) {
            return false;
        }
        return sections.some(x => this.isSectionIgnored(x));
    }

    public resolveOptionalSections(serverSideModel: ReadonlyModelChunk | IReadonlyModelHunk): void {
        if (this.allOptionalSections.size > 0) {
            const pathsWithMask = Iterator.from(ModelHunk.iterateEntries(serverSideModel))
                .filter(
                    /* eslint-disable no-warning-comments */
                    // TODO: временно ставлю сюда этот костыль т.к. при включенной серверной нормализации
                    // TODO: не удается корректно заполнять children для опциональных разделов и
                    // TODO: поэтому отключаем просмотр children полей до тех пора не сделаем https://yt.skbkontur.ru/issue/FS-7374
                    /* eslint-enable no-warning-comments */
                    ([path, viewModel]) => this.dataDeclarationHelper.hasMask(path) && !this.hasChildrenField(viewModel)
                )
                .map(([path]) => path);

            for (const path of pathsWithMask) {
                const sections = this.dataDeclarationHelper.getSections(path);

                for (const sectionName of sections) {
                    this.removeSectionFromIgnore(sectionName);
                }
            }
        }
    }

    private hasChildrenField(viewModel: ViewModel) {
        return viewModel.children != null;
    }

    public paths(model: IFormReadonlyModelAccessor, section: SectionName): Iterable<AbsoluteResolvedModelPath> {
        return Iterator.from(this.masks(section)).flatMap(x => MaskResolver.resolvePath(model, x));
    }

    public masks(section: SectionName): Iterable<AbsoluteModelPath> {
        const sectionMask = getSectionMaskBySectionName(section);
        const [_sectionName, ...instances] = section.split("-");
        const sectionMasks = this.section2masks.get(sectionMask)?.values() ?? [];

        return Iterator.from(sectionMasks)
            .map(mask => mask.applyInstancesDirty(instances))
            .filter(mask => !this.isIgnoredPathByMasks(mask));
    }

    public getSectionPathsForCalculations(
        model: IFormReadonlyModelAccessor,
        section: SectionName
    ): Iterable<AbsoluteResolvedModelPath> {
        const unitId = sectionNameToUnitId(section);
        const rootSectionName = unitId.pageId as SectionName;
        const sectionInfo = this.allSingleSections.get(rootSectionName);
        if (sectionInfo == undefined) {
            return Iterator.from(this.masks(section)).flatMap(x => MaskResolver.resolvePath(model, x));
        }
        const allInstances = getAllUnitIdInstances(unitId);

        if (allInstances.length < sectionInfo.requiredInstancesCount) {
            return [];
        }

        const sections = Iterator.from(this.allSingleSections.entries())
            .filter(
                ([_, value]) =>
                    value.path.startsWith(sectionInfo.path) &&
                    value.requiredInstancesCount == sectionInfo.requiredInstancesCount
            )
            .map(([sectionId]) => [sectionId, ...allInstances].join("-") as SectionName);

        return sections.flatMap(x => this.masks(x)).flatMap(x => MaskResolver.resolvePath(model, x));
    }

    public getSectionPrefix(section: SectionName): AbsoluteResolvedModelPath | undefined {
        const unitId = sectionNameToUnitId(section);
        const rootSectionName = unitId.pageId as SectionName;
        const sectionInfo = this.allSingleSections.get(rootSectionName);
        if (sectionInfo == undefined) {
            return undefined;
        }
        const allInstances = getAllUnitIdInstances(unitId);

        if (allInstances.length < sectionInfo.requiredInstancesCount) {
            return undefined;
        }
        const resolvedPath = sectionInfo.path.applyInstancesDirty(allInstances);
        return createAbsoluteResolvedFromTokens(resolvedPath.getSimpleTokensAsArrayStrict());
    }
}

function getSectionMaskBySectionName(sectionName: SectionName): SectionName {
    const [sectionId, ...instances] = sectionName.split("-");
    let result: string;
    if (instances.length == 0) {
        result = sectionId ?? reject();
    } else {
        result = `${sectionId}-${Array(instances.length).fill("*").join("-")}`;
    }
    return result as SectionName;
}
